/**
 * @file     AboutThisHomeSections.tsx
 * @purpose  Redesigned "About this home" deep-dive sections + field-level VOW
 *           gating. Render these AFTER the existing ListingSpecsGrid (which keeps
 *           the public description + property-detail/location stat cards +
 *           highlights). Replaces what AdditionalDetails showed, and adds the
 *           new VOW content.
 *
 *           Gating: public fields render normally; VOW values render inside
 *           <GatedValue gated={!isAuthenticated}> which blurs + locks them, and a
 *           single <VowUnlockCard> sits above the deep dives when gated.
 *
 *           additions), the page's `isAuthenticated` flag.
 *
 * Matches house style from ListingSpecsGrid.tsx: ACCENT #4DB2BA, section label
 * sx (0.7rem / 700 / 0.06em / uppercase / opacity .38), 1px divider.
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import { colorPalettesByMode } from '@/design-system/tokens/colors';
import * as React from 'react';
import Link from 'next/link';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { ListingIcon } from '@/components/icons/ListingIcons';
import { useAnalytics } from '@/hooks/useAnalytics';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { analyticsEvents } from '@/core/constants/analytics';
import type { ListingDetail } from '@/services/mls/mlsPropertyService';
import {
  buildRoomGroups,
  buildFinishedAreaSplit,
  buildBuildingFacts,
  buildLotLegal,
} from './listingSpecs';

const ACCENT = '#4DB2BA';
/** Glyphs and text: ACCENT is 2.5:1 on a light surface; the strong accent clears 3:1 in both themes. */
const ACCENT_INK = 'var(--r-accent-strong)';
/** White glyphs and labels sit on the strong accent fill, dark enough in both themes. */
const ACCENT_STRONG_FILL = colorPalettesByMode.light.accent[700];

/* ── Field-level gate ───────────────────────────────────────────────────────── */

function GatedValue({ gated, children }: { gated: boolean; children: React.ReactNode }) {
  return (
    <Box
      component="span"
      aria-hidden={gated || undefined}
      sx={{
        filter: gated ? 'blur(5px)' : 'none',
        transition: 'filter .35s',
        userSelect: gated ? 'none' : undefined,
        pointerEvents: gated ? 'none' : undefined,
      }}
    >
      {children}
    </Box>
  );
}

function MemberTag({ show, isDark }: { show: boolean; isDark: boolean }) {
  if (!show) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: ACCENT_INK,
        bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.09),
        border: `1px solid ${alpha(ACCENT, 0.2)}`,
        px: 0.9,
        py: 0.25,
        borderRadius: `${layoutTokens.radius.pill}px`,
      }}
    >
      <Box component="svg" viewBox="0 0 24 24" sx={{ width: 9, height: 9 }} fill="currentColor">
        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
      </Box>
      Member
    </Box>
  );
}

/** The single unlock CTA shown above the deep dives when gated. Wire the buttons
 *  to your existing auth gate (AuthGateController / sign-in modal). */
function VowUnlockCard({ isDark, onUnlock, listingKey }: { isDark: boolean; onUnlock?: () => void; listingKey?: string | null }) {
  const { trackEvent } = useAnalytics();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const impressionFired = React.useRef(false);

  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el || impressionFired.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !impressionFired.current) {
            impressionFired.current = true;
            reltaTrack('auth_gate.panel_locked.shown', {
              panel: 'about_this_home_vow',
              ...(listingKey ? { listing_key: listingKey } : {}),
            });
            trackEvent({
              eventName: analyticsEvents.propertyGateViewed,
              eventType: 'section',
              category: 'property_detail',
              label: 'about_this_home_vow',
              metadata: { panel: 'about_this_home_vow', listing_key: listingKey ?? null, surface: 'listing_detail' },
            });
            obs.disconnect();
            return;
          }
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [listingKey, trackEvent]);

  const handleUnlock = (cta: 'sign_in' | 'create_account') => {
    reltaTrack('auth_gate.panel_locked.unlock_clicked', {
      panel: 'about_this_home_vow',
      ...(listingKey ? { listing_key: listingKey } : {}),
    });
    trackEvent({
      eventName: analyticsEvents.propertyGateUnlockClicked,
      eventType: 'click',
      category: 'property_detail',
      label: 'about_this_home_vow',
      metadata: { panel: 'about_this_home_vow', cta, listing_key: listingKey ?? null, surface: 'listing_detail' },
    });
    onUnlock?.();
  };

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: `${layoutTokens.radius.lg}px`,
        border: `1px solid ${alpha(ACCENT, 0.22)}`,
        bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.07),
        p: '22px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        flexWrap: 'wrap',
        mt: 3,
      }}
    >
      <Box sx={{ width: 46, height: 46, borderRadius: `${layoutTokens.radius.md}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: ACCENT_STRONG_FILL, flexShrink: 0 }}>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 23, height: 23 }} fill="#fff">
          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm3 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '1.0625rem', mb: 0.25 }}>A few details are member-only</Typography>
        <Typography sx={{ fontSize: '0.84rem', lineHeight: 1.5, color: 'text.secondary' }}>
          Room-by-room dimensions, the level-by-level area split, construction &amp; systems, zoning, PID and legal. Free to reveal with an account.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Box component={Link} href="/sign-in" onClick={() => handleUnlock('sign_in')} sx={{ textDecoration: 'none', cursor: 'pointer', font: 'inherit', fontSize: '0.84rem', fontWeight: 600, px: 2.5, py: 1, borderRadius: `${layoutTokens.radius.md}px`, bgcolor: ACCENT_STRONG_FILL, color: '#fff' }}>Sign in</Box>
        <Box component={Link} href="/sign-up" onClick={() => handleUnlock('create_account')} sx={{ textDecoration: 'none', border: `1px solid ${alpha(ACCENT, 0.3)}`, cursor: 'pointer', font: 'inherit', fontSize: '0.84rem', fontWeight: 500, px: 2.25, py: 1, borderRadius: `${layoutTokens.radius.md}px`, bgcolor: 'transparent', color: ACCENT_INK }}>Create free account</Box>
      </Stack>
    </Box>
  );
}

/* ── Section header (icon chip + eyebrow + title + optional member tag) ──────── */

function SectionHeader({
  icon, eyebrow, title, gated, isDark,
}: { icon: React.ReactNode; eyebrow: string; title: string; gated: boolean; isDark: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6, mb: 3, flexWrap: 'wrap' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: `${layoutTokens.radius.md}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(ACCENT, 0.08), border: `1px solid ${alpha(ACCENT, 0.16)}`, color: ACCENT_INK, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: ACCENT_INK }}>{eyebrow}</Typography>
        <Typography component="h3" sx={{ fontSize: '1.1875rem', fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</Typography>
      </Box>
      <MemberTag show={gated} isDark={isDark} />
    </Box>
  );
}

/* ── Main component ─────────────────────────────────────────────────────────── */

export type AboutThisHomeSectionKey = 'area' | 'rooms' | 'construction' | 'lot';

interface Props {
  listing: ListingDetail;
  /** Pass the page's auth flag. When false, VOW fields blur + unlock card shows. */
  isAuthenticated: boolean;
  /** Optional: open your sign-in modal. */
  onUnlock?: () => void;
  /** Which subsections to render. Defaults to all. */
  sections?: readonly AboutThisHomeSectionKey[];
}

export function AboutThisHomeSections({ listing, isAuthenticated, onUnlock, sections }: Props) {
  const show = (key: AboutThisHomeSectionKey) => !sections || sections.includes(key);
  const { mode } = useAppTheme();
  const isDark = mode === 'dark';
  const gated = !isAuthenticated;
  const divider = `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`;
  const sunk = isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.025)';

  const area = buildFinishedAreaSplit(listing);
  const groups = buildRoomGroups(listing);
  const { pub, vow } = buildBuildingFacts(listing);
  const lot = buildLotLegal(listing);

  const factRow = (label: string, value: React.ReactNode, isVow: boolean) => (
    <Stack key={label} direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 2, py: 1.4, borderBottom: divider }}>
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
      <Typography component="span" sx={{ fontSize: '0.84rem', fontWeight: 500, textAlign: 'right' }}>
        {isVow ? <GatedValue gated={gated}>{value}</GatedValue> : value}
      </Typography>
    </Stack>
  );

  return (
    <Box>
      {gated && <VowUnlockCard isDark={isDark} onUnlock={onUnlock} listingKey={listing.listingKey} />}

      {/* ── Space & layout (total = public; split = VOW) ── */}
      {show('area') && area && (
        <Box sx={{ mt: 3.75, pt: 3.75, borderTop: divider }}>
          <SectionHeader icon={<ListingIcon name="area" size={18} />} eyebrow="Space & layout" title={`How the ${area.total} ft² breaks down`} gated={gated} isDark={isDark} />
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.disabled' }}>Finished area</Typography>
              <Typography sx={{ fontSize: '1.875rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15, mt: 0.4 }}>
                {area.total}<Box component="span" sx={{ fontSize: '1rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>ft²</Box>
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Level-by-level split</Typography>
              <MemberTag show={gated} isDark={isDark} />
            </Stack>
          </Stack>
          <GatedValue gated={gated}>
            <Box sx={{ display: 'flex', height: 15, borderRadius: `${layoutTokens.radius.pill}px`, overflow: 'hidden', gap: '2px' }}>
              {area.segments.map((s) => (
                <Box key={s.key} sx={{ width: `${s.pct}%`, bgcolor: s.key === 'a' ? ACCENT : alpha(ACCENT, 0.45) }} />
              ))}
            </Box>
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 3, mt: 2 }}>
              {area.segments.map((s) => (
                <Stack key={s.key} direction="row" alignItems="center" spacing={1.1}>
                  <Box sx={{ width: 11, height: 11, borderRadius: `${layoutTokens.radius.sm}px`, bgcolor: s.key === 'a' ? ACCENT : alpha(ACCENT, 0.45) }} />
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 500 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{s.value} · {s.pct}%</Typography>
                </Stack>
              ))}
            </Stack>
          </GatedValue>
        </Box>
      )}

      {/* ── Room by room (names visible; dimensions VOW) ── */}
      {show('rooms') && groups.length > 0 && (
        <Box sx={{ mt: 3.75, pt: 3.75, borderTop: divider }}>
          <SectionHeader icon={<ListingIcon name="lot" size={18} />} eyebrow="Room by room" title="Room sizes" gated={gated} isDark={isDark} />
          {groups.map((g) => (
            <Box key={g.level} sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1.1, borderBottom: `2px solid ${alpha(ACCENT, 0.2)}` }}>
                <Typography sx={{ fontSize: '0.7188rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{g.level}</Typography>
                <Typography sx={{ fontSize: '0.7188rem', fontWeight: 500, color: 'text.disabled' }}>
                  <GatedValue gated={gated}>{g.count}{g.sqft ? ` · ${g.sqft}` : ''}</GatedValue>
                </Typography>
              </Stack>
              {g.items.map((r, i) => (
                <Stack key={`${r.name}-${i}`} direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 2, py: 1.35, borderBottom: divider }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{r.name}</Typography>
                  <Typography component="span" sx={{ fontSize: '0.84rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <GatedValue gated={gated}>{r.dim}</GatedValue>
                  </Typography>
                </Stack>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* ── Construction & systems (Heating public; rest VOW) ── */}
      {show('construction') && (pub.length > 0 || vow.length > 0) && (
        <Box sx={{ mt: 3.75, pt: 3.75, borderTop: divider }}>
          <SectionHeader icon={<ListingIcon name="house" size={18} />} eyebrow="Construction & systems" title="How it’s built & serviced" gated={gated} isDark={isDark} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 5 }}>
            {pub.map((f) => factRow(f.label, f.value, false))}
            {vow.map((f) => factRow(f.label, f.value, true))}
          </Box>
        </Box>
      )}

      {/* ── Lot, zoning & legal (lot size + tax year public; rest VOW) ── */}
      {show('lot') && (
      <Box sx={{ mt: 3.75, pt: 3.75, borderTop: divider }}>
        <SectionHeader icon={<ListingIcon name="pin" size={18} />} eyebrow="Lot, zoning & legal" title="What you are buying" gated={gated} isDark={isDark} />
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.4, mb: 2 }}>
          {lot.zoning && (
            <Box sx={{ flex: 1, minWidth: 150, bgcolor: alpha(ACCENT, 0.07), border: `1px solid ${alpha(ACCENT, 0.16)}`, borderRadius: `${layoutTokens.radius.lg}px`, p: '15px 17px' }}>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: ACCENT_INK, mb: 0.6 }}>Zoning</Typography>
              <Typography sx={{ fontSize: '1.375rem', fontWeight: 600 }}><GatedValue gated={gated}>{lot.zoning}</GatedValue></Typography>
            </Box>
          )}
          {lot.lotSize && (
            <Box sx={{ flex: 1, minWidth: 150, bgcolor: sunk, border: divider, borderRadius: `${layoutTokens.radius.lg}px`, p: '15px 17px' }}>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.disabled', mb: 0.6 }}>Lot size</Typography>
              <Typography sx={{ fontSize: '1.375rem', fontWeight: 600 }}>{lot.lotSize}</Typography>
              {lot.lotDims && <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.25 }}>{lot.lotDims} · lane access</Typography>}
            </Box>
          )}
        </Stack>
        {lot.taxYear && factRow('Tax year', lot.taxYear, false)}
        {lot.vowRows.map((f) => factRow(f.label, f.value, true))}
        {lot.legal && (
          <Box sx={{ mt: 1.6 }}>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.disabled', mb: 0.6 }}>Legal description</Typography>
            <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.55, color: 'text.secondary' }}>
              <GatedValue gated={gated}>{lot.legal}</GatedValue>
            </Typography>
          </Box>
        )}
      </Box>
      )}
    </Box>
  );
}
